import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { geographyApi, type GeographyCatalogItem } from "@/services/api-v2.service";
import { cn } from "@/lib/utils";

const DATASETS = [
  { id: "atd", label: "АТД" },
  { id: "fo", label: "ФО" },
  { id: "vo", label: "ВО" },
  { id: "continent", label: "Континенты" },
] as const;

type DatasetId = (typeof DATASETS)[number]["id"];

interface UdgspzGeographyAttachDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onAttach: (unit: GeographyCatalogItem) => void;
}

export function UdgspzGeographyAttachDialog({
  open,
  onOpenChange,
  pending,
  onAttach,
}: UdgspzGeographyAttachDialogProps) {
  const [dataset, setDataset] = useState<DatasetId>("atd");
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const searching = trimmed.length >= 2;

  const listQuery = useQuery({
    queryKey: ["udgspz-geography-attach", dataset, searching ? trimmed : "roots"],
    enabled: open,
    queryFn: () =>
      searching
        ? geographyApi.catalog.search(trimmed, dataset)
        : geographyApi.catalog.roots(dataset),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Граница территории</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Контур копируется в схему выбранной сущности. Повтор той же единицы не создаёт второй контур.
        </p>
        <div className="flex flex-wrap gap-1">
          {DATASETS.map((item) => (
            <Button
              key={item.id}
              type="button"
              size="sm"
              variant={dataset === item.id ? "default" : "outline"}
              className="h-8"
              onClick={() => setDataset(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </div>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Поиск от 2 символов, иначе — корни справочника"
          aria-label="Поиск территории"
        />
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {(listQuery.data ?? []).map((unit) => (
            <li key={unit.id}>
              <button
                type="button"
                disabled={pending}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted",
                  pending && "opacity-60",
                )}
                onClick={() => onAttach(unit)}
              >
                <span className="truncate">{unit.name}</span>
                <span className="ml-2 shrink-0 text-[11px] text-muted-foreground">добавить</span>
              </button>
            </li>
          ))}
          {!listQuery.isLoading && (listQuery.data ?? []).length === 0 ? (
            <li className="px-2 py-3 text-xs text-muted-foreground">Ничего не найдено</li>
          ) : null}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
